import { Component, OnInit } from '@angular/core';
import { MatLegacyDialogRef } from "@angular/material/legacy-dialog";
import { CtButtonConfiguration, MAT_RAISED_PRIMARY, MAT_RAISED_WARN } from "@ctsolution/ct-framework";
import { TranslateService } from '@ngx-translate/core';

/**
 * Chiave per salvare la preferenza "non mostrare più" nel localStorage
 */
const HIDE_NAME_WARNING_KEY = 'work_order_hide_name_change_warning';

/**
 * Risultato del dialog di warning per la modifica del nome work order
 */
export interface WorkOrderNameChangeWarningResult {
  /** L'utente ha confermato di voler procedere con la modifica */
  confirmed: boolean;
  /** L'utente ha selezionato "non mostrare più" */
  hideWarningInFuture: boolean;
}

/**
 * Componente modale per avvisare l'utente quando il nome/numero dell'offerta viene modificato.
 * La modifica manuale potrebbe causare perdita di sequenzialità nella numerazione progressiva.
 * Include un checkbox "Non mostrare più" che salva la preferenza in localStorage.
 */
@Component({
  selector: 'ctm-work-order-name-change-warning-dialog',
  templateUrl: './work-order-name-change-warning-dialog.component.html',
  styleUrls: ['./work-order-name-change-warning-dialog.component.scss']
})
export class WorkOrderNameChangeWarningDialogComponent implements OnInit {

  /** Configurazione del pulsante di conferma */
  confirmButton!: CtButtonConfiguration;

  /** Configurazione del pulsante di annullamento */
  cancelButton!: CtButtonConfiguration;

  /** Flag per il checkbox "Non mostrare più questo messaggio" */
  hideWarningInFuture: boolean = false;

  constructor(
    private dialogRef: MatLegacyDialogRef<WorkOrderNameChangeWarningDialogComponent>,
    private translateService: TranslateService
  ) {}

  /**
   * Inizializzazione del componente
   * Configura i pulsanti con le traduzioni
   */
  ngOnInit(): void {
    this.initializeButtons();
  }

  /**
   * Inizializza i pulsanti con le etichette tradotte
   */
  private initializeButtons(): void {
    
    // Configurazione del pulsante di conferma - procede con la modifica
    this.confirmButton = CtButtonConfiguration
      .create()
      .setLabel(this.translateService.instant('CT_PAGE.CT_FORM.confirm') || 'Conferma')
      .setAction(() => this.onConfirm())
      .setMatherialOptions(MAT_RAISED_PRIMARY);

    // Configurazione del pulsante di annullamento - torna indietro
    this.cancelButton = CtButtonConfiguration
      .create()
      .setLabel(this.translateService.instant('CT_PAGE.CT_FORM.cancel') || 'Annulla')
      .setAction(() => this.onCancel())
      .setMatherialOptions(MAT_RAISED_WARN);
  }

  /**
   * Gestisce la conferma dell'operazione
   * L'utente ha confermato di voler procedere con la modifica del nome
   * Se il checkbox è selezionato, salva la preferenza in localStorage
   */
  onConfirm(): void {
    // Se l'utente ha selezionato "non mostrare più", salva nel localStorage
    if (this.hideWarningInFuture) {
      this.saveHideWarningPreference();
    }

    const result: WorkOrderNameChangeWarningResult = {
      confirmed: true,
      hideWarningInFuture: this.hideWarningInFuture
    };
    
    this.dialogRef.close(result);
  }

  /**
   * Gestisce l'annullamento dell'operazione
   * L'utente ha deciso di non procedere con la modifica
   */
  onCancel(): void {
    const result: WorkOrderNameChangeWarningResult = {
      confirmed: false,
      hideWarningInFuture: false
    };
    
    this.dialogRef.close(result);
  }

  /**
   * Salva la preferenza "non mostrare più" nel localStorage
   */
  private saveHideWarningPreference(): void {
    try {
      localStorage.setItem(HIDE_NAME_WARNING_KEY, 'true');
    } catch (error) {
      // Gestione errore silenzioso - se localStorage non è disponibile, continua comunque
      console.warn('Impossibile salvare la preferenza nel localStorage:', error);
    }
  }

  /**
   * Metodo statico per verificare se l'utente ha scelto di nascondere il warning
   * Viene utilizzato dal componente chiamante per decidere se aprire o meno il dialog
   * @returns true se l'utente ha scelto di nascondere il warning, false altrimenti
   */
  static shouldHideWarning(): boolean {
    try {
      return localStorage.getItem(HIDE_NAME_WARNING_KEY) === 'true';
    } catch (error) {
      // Se localStorage non è disponibile, mostra sempre il warning per sicurezza
      console.warn('Impossibile leggere dal localStorage:', error);
      return false;
    }
  }

  /**
   * Metodo statico per resettare la preferenza (utile per test o reset configurazione)
   */
  static resetHideWarningPreference(): void {
    try {
      localStorage.removeItem(HIDE_NAME_WARNING_KEY);
    } catch (error) {
      console.warn('Impossibile rimuovere la preferenza dal localStorage:', error);
    }
  }
}
